/**
 * What the reading page knows about where the student is, for the book tutor.
 *
 * The page shows every chapter of the module one after another, each in a
 * <section class="chapter-section" data-chapter="N"> (N = the chapter's place in
 * the module, the same order the tutor reads them in). Inside, the chapter's
 * own blocks carry `data-section` ids.
 */

import { describeRange } from "../Tutor/anchors";

const CHAPTER = "section.chapter-section[data-chapter]";

const chapterOf = (el) => {
  const ch = el?.closest?.(CHAPTER);
  const n = ch ? Number(ch.getAttribute("data-chapter")) : NaN;
  return Number.isInteger(n) ? n : null;
};

/** Outermost data-section blocks only: a nested one is part of its parent. */
const blocks = (root) => [...root.querySelectorAll("[data-section]")]
  .filter((el) => !el.parentElement?.closest("[data-section]"));

/**
 * The sections in view, nearest the middle of the scrolling pane first, and the
 * chapter of the one nearest the middle. { chapter, sections: [{id, chapter}] }
 */
export function placeIn(root, pane) {
  if (!root) return { chapter: null, sections: [] };
  const view = (pane || document.documentElement).getBoundingClientRect();
  const top = pane ? view.top : 0;
  const bottom = pane ? view.bottom : window.innerHeight;
  const middle = (top + bottom) / 2;
  const seen = [];
  for (const el of blocks(root)) {
    const r = el.getBoundingClientRect();
    if (r.bottom <= top || r.top >= bottom || r.height === 0) continue;
    seen.push({ id: el.getAttribute("data-section"), chapter: chapterOf(el),
      d: Math.abs((Math.max(r.top, top) + Math.min(r.bottom, bottom)) / 2 - middle) });
  }
  seen.sort((a, b) => a.d - b.d);
  let chapter = seen[0]?.chapter ?? null;
  if (chapter === null) {
    // A chapter with no section ids: the chapter in view, at least.
    const chapters = [...root.querySelectorAll(CHAPTER)];
    const inView = chapters.find((c) => { const r = c.getBoundingClientRect(); return r.top < middle && r.bottom > middle; });
    chapter = inView ? chapterOf(inView) : null;
  }
  return { chapter, sections: seen.map(({ id, chapter: c }) => ({ id, chapter: c })) };
}

/**
 * The selection inside `root` as { text, section, chapter }, or null. With the
 * words around it (`prefix`, `suffix`, `around`, from ../Tutor/anchors) when
 * it lies within one block, so a tab branched from it can find it again.
 */
export function selectionIn(root) {
  const sel = window.getSelection?.();
  if (!root || !sel || sel.isCollapsed || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  const node = range.commonAncestorContainer;
  const el = node.nodeType === 1 ? node : node.parentElement;
  if (!el || !root.contains(el)) return null;
  const text = sel.toString().replace(/\s+\n/g, "\n").trim();
  if (text.length < 3) return null;
  const startEl = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
  const block = startEl?.closest?.("[data-section]");
  const outer = block ? (blocks(root).find((b) => b.contains(block)) || block) : null;
  // The words around the passage come from its section block; a chapter without
  // section ids (older reading material) uses the paragraph, else the chapter.
  const scope = outer || startEl?.closest?.("p, li, pre, blockquote, td, section, article") || el;
  const anchor = (scope && scope.contains(range.endContainer) ? describeRange(range, scope) : null) || {};
  return { text: text.slice(0, 4000), section: outer?.getAttribute("data-section") || "",
    chapter: chapterOf(startEl || el), prefix: anchor.prefix || "", suffix: anchor.suffix || "", around: anchor.around || "" };
}

/** Scroll the book to a source (chapter + section) and flash it. False if it isn't on the page. */
export function revealSource(root, source) {
  if (!root || !Number.isInteger(source?.chapter_index)) return false;
  const chapter = root.querySelector(`${CHAPTER.replace("[data-chapter]", "")}[data-chapter="${source.chapter_index}"]`);
  if (!chapter) return false;
  let target = chapter;
  if (source.section) {
    const esc = window.CSS?.escape ? window.CSS.escape(source.section) : source.section.replace(/"/g, '\\"');
    target = chapter.querySelector(`[data-section="${esc}"]`) || chapter;
  }
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  if (target.animate) {
    target.animate([{ backgroundColor: "rgba(1,114,166,0.16)" }, { backgroundColor: "transparent" }],
      { duration: 1800, easing: "ease-out" });
  }
  return true;
}
